import { html, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../utils/ui-components";
import { SearchResult } from "../server/media";
import { aiIcon } from "../utils/icons";
import { AudioState } from "./audio-player";

@customElement("search-view")
export class SearchView extends BaseElement {
    @property()
    searchResult: SearchResult = { hits: [], pathFacets: [], total: 0 };

    @property()
    loading = false;

    @property()
    query = "";

    @property()
    startDate = new Date();

    @property()
    endDate = new Date();

    @property()
    onSearch?: (query: string) => void;

    @property()
    onDateChange?: (startDate: Date, endDate: Date) => void;

    @property()
    onAudioRequest?: (state: AudioState) => void;

    @property()
    hasMore = false;

    @property()
    onLoadMore?: () => void;

    @property()
    onAddAllToContext?: () => void;

    @state()
    private showScrollToTop = false;

    private searchTimeout: any;

    private observer?: IntersectionObserver;

    private scrollObserver?: IntersectionObserver;

    private setupIntersectionObserver() {
        this.observer?.disconnect();

        const target = this.renderRoot.querySelector("#scroll-sentinel");
        const scrollContainer = this.renderRoot.querySelector(".search-view-container");
        if (!target || !scrollContainer) return;

        this.observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !this.loading && this.hasMore) {
                    this.onLoadMore?.();
                }
            },
            {
                root: scrollContainer,
                threshold: 0.1,
                rootMargin: "200px 0px",
            }
        );

        this.observer.observe(target);
    }

    private setupScrollObserver() {
        this.scrollObserver?.disconnect();

        const searchForm = this.renderRoot.querySelector(".search-form");
        const scrollContainer = this.renderRoot.querySelector(".search-view-container");
        if (!searchForm || !scrollContainer) return;

        this.scrollObserver = new IntersectionObserver(
            (entries) => {
                requestAnimationFrame(() => {
                    this.showScrollToTop = !entries[0].isIntersecting;
                });
            },
            {
                root: scrollContainer,
                threshold: 0,
            }
        );

        this.scrollObserver.observe(searchForm);
    }

    private scrollToTop = () => {
        this.renderRoot.querySelector(".search-view-container")?.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    updated(changedProperties: PropertyValues) {
        super.updated(changedProperties);
        if (changedProperties.has("searchResult")) {
            this.setupIntersectionObserver();
        }
        this.setupScrollObserver(); // Call this every update to ensure it's set up
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.observer?.disconnect();
        this.scrollObserver?.disconnect();
    }

    private handleSearchInput = (e: Event) => {
        if (e instanceof KeyboardEvent && e.key === "Enter") {
            this.executeSearch();
            return;
        }
        this.query = this.querySelector<HTMLInputElement>("#query")!.value.trim();
    };

    private executeSearch = () => {
        this.onSearch?.(this.query);
    };

    private handleDateChange = () => {
        this.startDate = new Date(this.querySelector<HTMLInputElement>("#startDate")!.value);
        this.endDate = new Date(this.querySelector<HTMLInputElement>("#endDate")!.value);
        this.onDateChange?.(this.startDate, this.endDate);
    };

    private handleDateInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const maxDate = input.max;

        // Upper cap startDate to EndDate when using arrow keys
        if (input.value >= maxDate) {
            input.value = maxDate;
        }
    }

    private handleCalendarClick(inputId: string) {
        this.querySelector<HTMLInputElement>(`#${inputId}`)?.showPicker();
    }

    private addAllToContext = () => {
        this.onAddAllToContext?.();
    };

    render() {
        return html`
            <div class="h-full flex flex-col relative">
                <div class="flex-1 overflow-y-auto search-view-container overscroll-contain will-change-scroll">
                    <div class="flex flex-col gap-4 p-4 pb-16 lg:pb-4">
                        <!-- Search Form -->
                        <div class="flex flex-col gap-4 search-form">
                            <!-- Search Input -->
                            <div class="relative flex gap-2">
                                <div class="relative flex-1">
                                    <input
                                        id="query"
                                        @input=${this.handleSearchInput}
                                        @keydown=${this.handleSearchInput}
                                        class="w-full h-11 sm:h-12 pl-11 sm:pl-12 pr-4 rounded-lg sm:rounded-xl bg-gray-50 border-0
                                        text-gray-900 placeholder:text-gray-400 text-base
                                        focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white
                                        transition-all duration-200"
                                        type="search"
                                        placeholder="Search ..."
                                        .value=${this.query}
                                    />
                                    <svg
                                        class="absolute left-3.5 sm:left-4 top-3.5 w-5 h-5 text-gray-400"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                        />
                                    </svg>
                                </div>
                                <button
                                    @click=${this.executeSearch}
                                    class="h-11 sm:h-12 px-4 rounded-lg sm:rounded-xl bg-blue-600 text-white hover:bg-blue-700
                                    focus:outline-none focus:ring-2 focus:ring-blue-500/20
                                    transition-all duration-200"
                                >
                                    Search
                                </button>
                            </div>

                            <!-- Date Range Controls -->
                            <div class="flex flex-col gap-4">
                                <!-- Date Inputs -->
                                <div class="flex items-center gap-3">
                                    <div class="relative flex-1">
                                        <input
                                            id="startDate"
                                            @input=${this.handleDateInput}
                                            @change=${this.handleDateChange}
                                            type="date"
                                            .value=${getYearMonthDay(this.startDate)}
                                            .max=${getYearMonthDay(this.endDate)}
                                            class="w-full h-10 pl-10 pr-3 rounded-lg bg-gray-50 border-0
                                            text-gray-900 text-sm
                                            focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white
                                            transition-all duration-200"
                                        />
                                        <svg
                                            @click=${() => this.handleCalendarClick("startDate")}
                                            class="absolute left-3 top-2.5 w-5 h-5 text-gray-400 cursor-pointer hover:text-gray-600"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                            />
                                        </svg>
                                    </div>

                                    <span class="text-gray-400 text-sm font-medium">to</span>

                                    <div class="relative flex-1">
                                        <input
                                            id="endDate"
                                            @input=${this.handleDateInput}
                                            @change=${this.handleDateChange}
                                            type="date"
                                            .value=${getYearMonthDay(this.endDate)}
                                            .min=${getYearMonthDay(this.startDate)}
                                            .max=${getYearMonthDay(new Date())}
                                            class="w-full h-10 pl-10 pr-3 rounded-lg bg-gray-50 border-0
                                            text-gray-900 text-sm
                                            focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white
                                            transition-all duration-200"
                                        />
                                        <svg
                                            @click=${() => this.handleCalendarClick("endDate")}
                                            class="absolute left-3 top-2.5 w-5 h-5 text-gray-400 cursor-pointer hover:text-gray-600"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                stroke-width="2"
                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                            />
                                        </svg>
                                    </div>
                                </div>

                                <!-- Quick Date Filters -->
                                <div class="flex flex-wrap gap-1.5 sm:gap-2">
                                    ${["Today", "Yesterday", "Last Week", "Last Month"].map(
                                        (period) => html`
                                            <button
                                                @click=${() => this.handlePeriodClick(period)}
                                                class="px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm bg-gray-50 text-gray-600
                                                hover:bg-gray-100 transition-colors border border-gray-200"
                                            >
                                                ${period}
                                            </button>
                                        `
                                    )}
                                </div>
                            </div>
                        </div>

                        <!-- Results header -->
                        ${this.loading && !this.searchResult.hits.length
                            ? html`<div class="text-center text-gray-500">Loading...</div>`
                            : html`
                                  <div class="flex items-center justify-between">
                                      <div class="text-gray-600 font-medium">${this.searchResult.total} results found</div>
                                      ${this.searchResult.hits.length > 0
                                          ? html`
                                                <button
                                                    @click=${this.addAllToContext}
                                                    class="flex items-center gap-1.5 px-2.5 py-1 text-sm text-gray-500 hover:text-gray-700
                                                bg-gray-50 hover:bg-gray-100 rounded-full transition-colors border border-gray-200"
                                                >
                                                    <i class="w-3.5 h-3.5 icon">${aiIcon}</i>
                                                    <span>Add All to Chat</span>
                                                </button>
                                            `
                                          : ""}
                                  </div>

                                  <!-- Results list -->
                                  ${this.searchResult.hits.map(
                                      (hit) => html`<search-hit-view .hit=${hit} .onAudioRequest=${this.onAudioRequest}></search-hit-view>`
                                  )}
                                  ${this.hasMore ? html`<div id="scroll-sentinel" class="h-4"></div>` : ""}
                              `}
                    </div>
                </div>

                <!-- Scroll to top button - enhanced visibility -->
                ${this.showScrollToTop
                    ? html`
                          <button
                              @click=${this.scrollToTop}
                              class="absolute bottom-16 lg:bottom-4 left-4 p-2 bg-white/90 backdrop-blur-sm rounded-full
                                shadow-[0_2px_8px_rgba(0,0,0,0.1)] border border-gray-200
                                hover:bg-gray-50 hover:shadow-[0_4px_12px_rgba(0,0,0,0.15)]
                                transition-all duration-200"
                              aria-label="Scroll to top"
                          >
                              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                              </svg>
                          </button>
                      `
                    : ""}
            </div>
        `;
    }

    private handlePeriodClick(period: string) {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        this.endDate = today;

        switch (period) {
            case "Today":
                this.startDate = new Date();
                this.startDate.setHours(0, 0, 0, 0);
                break;
            case "Yesterday":
                this.startDate = new Date();
                this.startDate.setDate(this.startDate.getDate() - 1);
                this.startDate.setHours(0, 0, 0, 0);
                this.endDate = new Date(this.startDate);
                this.endDate.setHours(23, 59, 59, 999);
                break;
            case "Last Week":
                this.startDate = new Date();
                this.startDate.setDate(this.startDate.getDate() - 7);
                this.startDate.setHours(0, 0, 0, 0);
                break;
            case "Last Month":
                this.startDate = new Date();
                this.startDate.setDate(this.startDate.getDate() - 30);
                this.startDate.setHours(0, 0, 0, 0);
                break;
        }
        this.onDateChange?.(this.startDate, this.endDate);
    }
}

function getYearMonthDay(date: Date): string {
    return date.toISOString().split("T")[0];
}
